import { INK, type Sketch } from './sketch';

/**
 * Procedural placeholder art in the notebook style. Each doodle draws into a
 * 256×256 box (icons/particles into smaller boxes); different seeds re-trace
 * the same drawing for frame-based line boil. Heroes face right, monsters left.
 */
export type DoodleFn = (s: Sketch, size: number) => void;

const SKIN = '#ffe0bd';

function stickLegs(s: Sketch, x: number, y: number, spread = 16): void {
  s.line(x - 8, y, x - spread, 236);
  s.line(x + 8, y, x + spread, 236);
  s.line(x - spread, 236, x - spread - 12, 238);
  s.line(x + spread, 236, x + spread + 12, 238);
}

function face(s: Sketch, cx: number, cy: number, mood: 'happy' | 'grr' | 'calm' = 'happy'): void {
  s.dot(cx - 9, cy - 2, 3.2);
  s.dot(cx + 9, cy - 2, 3.2);
  if (mood === 'happy') s.path([[cx - 9, cy + 9], [cx, cy + 14], [cx + 9, cy + 9]], false, { width: 2.4 });
  else if (mood === 'grr') {
    s.line(cx - 15, cy - 11, cx - 4, cy - 7, { width: 2.5 });
    s.line(cx + 15, cy - 11, cx + 4, cy - 7, { width: 2.5 });
    s.path([[cx - 8, cy + 12], [cx - 3, cy + 9], [cx + 2, cy + 12], [cx + 8, cy + 9]], false, { width: 2.2 });
  } else s.line(cx - 6, cy + 11, cx + 6, cy + 11, { width: 2.2 });
}

function pencil(s: Sketch, x1: number, y1: number, x2: number, y2: number, w = 9): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy * w;
  const py = ux * w;
  const tipStart = 0.82;
  const ex = x1 + dx * 0.12;
  const ey = y1 + dy * 0.12;
  const tx = x1 + dx * tipStart;
  const ty = y1 + dy * tipStart;
  // eraser
  s.poly([[x1 + px, y1 + py], [ex + px, ey + py], [ex - px, ey - py], [x1 - px, y1 - py]], { fill: INK.pink, width: 2.4 });
  // body
  s.poly([[ex + px, ey + py], [tx + px, ty + py], [tx - px, ty - py], [ex - px, ey - py]], { fill: INK.yellow, width: 2.4 });
  // wood cone + graphite tip
  s.poly([[tx + px, ty + py], [x2, y2], [tx - px, ty - py]], { fill: '#e9c9a0', width: 2.4 });
  s.line(x2 - ux * 10, y2 - uy * 10, x2, y2, { width: 4, color: INK.black });
}

export const HERO_DOODLES: Record<string, DoodleFn> = {
  pencil_knight(s) {
    stickLegs(s, 128, 192);
    s.rect(100, 120, 56, 74, { fill: INK.blue });
    s.line(100, 150, 156, 150, { width: 2 });
    s.line(156, 136, 186, 150);
    pencil(s, 176, 172, 222, 50, 8);
    s.ellipse(92, 158, 22, 30, { fill: INK.red });
    s.star(92, 158, 11, { fill: INK.yellow, width: 2 });
    s.ellipse(128, 88, 30, 28, { fill: SKIN });
    s.path([[96, 84], [100, 60], [128, 52], [156, 60], [160, 84]], false, { width: 3.4 });
    s.crayonFill([[98, 82], [101, 60], [128, 53], [155, 60], [158, 82]], '#9aa7b0');
    s.scribble(116, 46, 26, 7, 3, { color: INK.red, width: 3 });
    face(s, 128, 92, 'happy');
  },
  crayon_mage(s) {
    stickLegs(s, 128, 220, 14);
    s.poly([[128, 112], [84, 226], [172, 226]], { fill: INK.purple });
    s.line(150, 150, 178, 132);
    s.poly([[176, 140], [198, 86], [208, 90], [186, 144]], { fill: INK.orange, width: 2.4 });
    s.star(205, 74, 13, { fill: INK.yellow, width: 2 });
    s.ellipse(128, 94, 26, 25, { fill: SKIN });
    s.poly([[94, 80], [162, 80], [140, 12]], { fill: INK.blue });
    s.star(126, 54, 7, { fill: INK.yellow, width: 1.6 });
    s.star(140, 34, 5, { fill: INK.yellow, width: 1.4 });
    face(s, 128, 98, 'happy');
  },
  eraser_ninja(s) {
    stickLegs(s, 128, 194, 20);
    s.rect(98, 120, 60, 76, { fill: INK.pink });
    s.rect(98, 166, 60, 30, { fill: INK.blue, passes: 1 });
    s.line(158, 140, 184, 128);
    s.star(194, 122, 13, { fill: '#b8c2cc', width: 2 });
    s.line(98, 140, 76, 158);
    s.ellipse(128, 90, 27, 26, { fill: '#4a4a4a' });
    s.rect(104, 80, 48, 16, { fill: SKIN, passes: 1, wobble: 0.8 });
    s.dot(120, 88, 3);
    s.dot(138, 88, 3);
    s.path([[103, 74], [80, 64], [62, 72]], false, { color: INK.red, width: 4 });
    s.path([[103, 76], [84, 80], [66, 92]], false, { color: INK.red, width: 4 });
    s.line(102, 74, 154, 72, { color: INK.red, width: 5 });
  },
  ruler_guard(s) {
    stickLegs(s, 124, 196, 18);
    s.rect(88, 116, 74, 82, { fill: INK.green });
    for (let i = 0; i < 6; i++) s.line(94 + i * 11, 116, 94 + i * 11, 126 + (i % 2) * 6, { width: 1.8, passes: 1 });
    s.rect(180, 36, 16, 204, { fill: '#e9c46a', width: 2.6 });
    for (let y = 48; y < 236; y += 14) s.line(180, y, 188 + ((y / 14) % 2) * 4, y, { width: 1.6, passes: 1 });
    s.line(162, 140, 182, 136);
    s.rect(62, 128, 40, 62, { fill: INK.blue });
    s.line(82, 134, 82, 184, { color: INK.white, width: 3, passes: 1 });
    s.ellipse(124, 88, 28, 27, { fill: SKIN });
    s.rect(94, 52, 60, 22, { fill: '#9aa7b0' });
    face(s, 124, 94, 'calm');
  },
  glue_alchemist(s) {
    stickLegs(s, 126, 200, 16);
    // lab coat with glue drips
    s.poly([[98, 118], [154, 118], [166, 204], [86, 204]], { fill: INK.white });
    s.line(126, 120, 126, 204, { width: 2, passes: 1 });
    for (const x of [96, 116, 150]) s.path([[x, 204], [x + 2, 214], [x - 1, 220]], false, { color: '#d8d8d8', width: 4, passes: 1 });
    // test tube held out front
    s.line(154, 140, 178, 128);
    s.poly([[176, 86], [192, 86], [192, 136], [184, 146], [176, 136]], { fill: '#f0f4ff', width: 2.4 });
    s.crayonFill([[177, 112], [191, 112], [191, 135], [184, 144], [177, 135]], INK.white);
    for (const [x, y] of [[182, 74], [190, 62], [180, 50]] as [number, number][]) s.ellipse(x, y, 5, 5, { width: 1.8 });
    // glue bottle on the belt
    s.rect(100, 176, 14, 22, { fill: INK.orange, width: 2 });
    s.ellipse(126, 88, 28, 27, { fill: SKIN });
    // goggles pushed up on the forehead
    s.ellipse(114, 66, 10, 8, { fill: INK.teal, width: 2.2 });
    s.ellipse(138, 66, 10, 8, { fill: INK.teal, width: 2.2 });
    s.line(124, 66, 128, 66, { width: 2 });
    face(s, 126, 94, 'happy');
  },
  highlighter_scout(s) {
    stickLegs(s, 128, 196, 22);
    s.rect(100, 118, 56, 80, { fill: INK.teal });
    s.rect(110, 150, 36, 16, { fill: '#2a8f86', passes: 1 });
    s.poly([[104, 104], [152, 104], [146, 124], [110, 124]], { fill: INK.teal });
    // neon highlighter dagger with glow streaks
    s.line(156, 140, 180, 124);
    s.poly([[176, 130], [214, 84], [224, 92], [186, 138]], { fill: '#c8f560', width: 2.4 });
    s.poly([[214, 84], [226, 72], [230, 82], [224, 92]], { fill: INK.yellow, width: 2 });
    for (let i = 0; i < 3; i++) s.line(198 + i * 10, 118 + i * 4, 230 + i * 6, 108 + i * 8, { color: '#c8f560', width: 3, passes: 1 });
    // rolled map in the pocket
    s.rect(104, 164, 10, 26, { fill: '#f4e3b2', width: 1.8 });
    s.ellipse(128, 86, 27, 26, { fill: SKIN });
    s.path([[100, 84], [104, 58], [128, 50], [152, 58], [156, 84]], false, { color: '#2a8f86', width: 4 });
    face(s, 128, 90, 'grr');
  },
};

export const MONSTER_DOODLES: Record<string, DoodleFn> = {
  gum_blob(s) {
    const body = s.ellipsePoints(128, 160, 86, 66, 22, 0.12);
    s.poly(body, { fill: INK.green, width: 3.4 });
    s.line(84, 210, 82, 236, { color: INK.green, width: 5 });
    s.dot(82, 238, 5, INK.green);
    s.line(170, 212, 172, 230, { color: INK.green, width: 5 });
    s.ellipse(112, 136, 24, 24, { fill: INK.white });
    s.dot(104, 138, 8);
    s.dot(101, 134, 2.5, INK.white);
    s.path([[86, 184], [98, 176], [110, 186], [122, 176], [134, 186], [146, 178]], false, { width: 3 });
    s.ellipse(168, 128, 10, 8, { fill: '#9bd389', width: 2 });
  },
  pencil_dragon(s) {
    s.path([[180, 176], [212, 160], [206, 196], [236, 186]], false, { width: 4 });
    s.poly([[120, 130], [150, 50], [176, 132]], { fill: INK.purple });
    s.ellipse(150, 168, 62, 40, { fill: INK.red });
    s.line(124, 200, 116, 236);
    s.line(170, 202, 178, 236);
    s.poly([[106, 150], [80, 112], [96, 106], [124, 140]], { fill: INK.red, width: 2.6 });
    s.ellipse(70, 104, 32, 24, { fill: INK.red });
    s.poly([[62, 82], [56, 62], [74, 78]], { fill: INK.yellow, width: 2 });
    s.dot(62, 98, 4);
    s.line(42, 112, 56, 114, { width: 2.4 });
    s.path([[38, 108], [20, 96], [30, 112], [8, 112], [28, 120], [16, 132]], false, { color: INK.orange, width: 4 });
    for (let i = 0; i < 4; i++) s.poly([[130 + i * 16, 130], [138 + i * 16, 116], [146 + i * 16, 130]], { fill: INK.yellow, width: 1.8, passes: 1 });
  },
  homework_ghost(s) {
    const pts: [number, number][] = [];
    for (let i = 0; i <= 12; i++) {
      const a = Math.PI + (i / 12) * Math.PI;
      pts.push([128 + Math.cos(a) * 74, 120 + Math.sin(a) * 84]);
    }
    for (let i = 0; i <= 8; i++) pts.push([202 - i * 18.5, 222 + (i % 2 === 0 ? 0 : -18)]);
    s.poly(pts, { fill: '#e8eef8', width: 3.2 });
    for (let y = 92; y < 210; y += 20) s.line(70, y, 186, y, { color: INK.lineBlue, width: 2, passes: 1, wobble: 0.6 });
    s.line(84, 60, 84, 214, { color: INK.margin, width: 2, passes: 1 });
    s.ellipse(106, 116, 10, 15, { fill: INK.black });
    s.ellipse(146, 116, 10, 15, { fill: INK.black });
    s.ellipse(126, 160, 13, 16, { fill: '#444' });
    s.text('F-', 176, 70, 38, INK.red, 0.2);
  },
  eraser_golem(s) {
    s.rect(84, 112, 92, 100, { fill: INK.pink });
    s.rect(84, 180, 92, 32, { fill: INK.blue, passes: 1 });
    s.rect(96, 52, 68, 58, { fill: INK.pink });
    s.rect(46, 118, 34, 70, { fill: INK.pink });
    s.rect(180, 118, 34, 70, { fill: INK.pink });
    s.rect(96, 212, 26, 26, { fill: INK.pink });
    s.rect(138, 212, 26, 26, { fill: INK.pink });
    face(s, 130, 82, 'grr');
    for (let i = 0; i < 7; i++) s.dot(60 + i * 24, 242 - (i % 3) * 3, 2.4, '#d9a0ad');
    s.text('PL', 130, 150, 30, '#c26a7f');
  },
  spitball_spider(s) {
    for (let i = 0; i < 4; i++) {
      const y = 140 + i * 18;
      s.path([[128, y], [80 - i * 6, y - 40 + i * 6], [44 - i * 4, y + 30 + i * 10]], false, { width: 3 });
      s.path([[128, y], [176 + i * 6, y - 40 + i * 6], [212 + i * 4, y + 30 + i * 10]], false, { width: 3 });
    }
    s.ellipse(128, 160, 58, 48, { fill: '#8fa3b8' });
    s.ellipse(128, 112, 30, 24, { fill: '#8fa3b8' });
    for (const [x, y] of [[116, 106], [128, 102], [140, 106], [122, 116], [134, 116]] as [number, number][]) {
      s.dot(x, y, 4.2, INK.white);
      s.dot(x - 1, y, 2.2);
    }
    s.line(120, 130, 116, 142, { width: 2.6 });
    s.line(136, 130, 140, 142, { width: 2.6 });
    for (let i = 0; i < 5; i++) s.dot(100 + i * 14, 170 + (i % 2) * 8, 3, '#6d7f92');
  },
  ink_octopus(s) {
    for (let i = 0; i < 5; i++) {
      const bx = 84 + i * 22;
      const pts: [number, number][] = [];
      for (let k = 0; k <= 6; k++) pts.push([bx + Math.sin(k * 1.1 + i) * 14, 160 + k * 12]);
      s.path(pts, false, { color: '#3d3a75', width: 9, passes: 1 });
      s.path(pts, false, { width: 2 });
    }
    s.ellipse(128, 118, 66, 60, { fill: '#5b57a6' });
    s.ellipse(106, 112, 14, 16, { fill: INK.white });
    s.ellipse(150, 112, 14, 16, { fill: INK.white });
    s.dot(102, 116, 6);
    s.dot(146, 116, 6);
    s.path([[112, 146], [128, 138], [144, 146]], false, { width: 2.6 });
    for (const [x, y] of [[40, 70], [214, 60], [30, 190], [222, 200]] as [number, number][]) s.ellipse(x, y, 6, 9, { fill: '#3d3a75', width: 1.6 });
  },
  stapler_crab(s) {
    // legs
    for (let i = 0; i < 3; i++) {
      s.path([[100 - i * 4, 180], [70 - i * 10, 196], [60 - i * 10, 230]], false, { width: 3 });
      s.path([[160 + i * 4, 180], [190 + i * 10, 196], [200 + i * 10, 230]], false, { width: 3 });
    }
    // stapler shell: base + hinged top
    s.rect(70, 160, 130, 30, { fill: '#7a2e2e' });
    s.poly([[64, 160], [70, 118], [196, 132], [204, 160]], { fill: '#b33a3a' });
    s.rect(70, 150, 20, 10, { fill: '#c8d0d8', width: 1.8, passes: 1 });
    // staple spikes along the top
    for (let i = 0; i < 5; i++) s.path([[86 + i * 22, 124 + i * 2], [86 + i * 22, 112 + i * 2], [96 + i * 22, 113 + i * 2], [96 + i * 22, 125 + i * 2]], false, { color: '#8a96a3', width: 2.6, passes: 1 });
    // snapping claw (faces left)
    s.poly([[64, 150], [28, 132], [20, 112], [44, 126]], { fill: '#b33a3a', width: 2.6 });
    s.poly([[64, 154], [26, 156], [16, 172], [44, 160]], { fill: '#b33a3a', width: 2.6 });
    // eyes on stalks
    s.line(110, 120, 104, 96, { width: 2.6 });
    s.line(134, 122, 136, 98, { width: 2.6 });
    s.dot(104, 92, 6, INK.white);
    s.dot(102, 92, 3);
    s.dot(136, 94, 6, INK.white);
    s.dot(134, 94, 3);
  },
  glue_monarch(s) {
    const body = s.ellipsePoints(128, 168, 92, 62, 24, 0.1);
    s.poly(body, { fill: '#f4f6fb', width: 3.4 });
    for (const x of [62, 98, 160, 196]) s.path([[x, 208], [x + 2, 226], [x - 2, 236]], false, { color: '#e1e5ee', width: 6, passes: 1 });
    s.ellipse(128, 238, 70, 8, { fill: '#e8ebf2', width: 1.6, passes: 1 });
    // glue cap + paper crown
    s.rect(112, 92, 32, 18, { fill: INK.orange, width: 2.4 });
    s.poly([[86, 104], [92, 66], [108, 86], [128, 58], [148, 86], [164, 66], [170, 104]], { fill: INK.yellow, width: 2.6 });
    s.dot(128, 76, 4, INK.red);
    // smug face (looking left)
    s.path([[86, 150], [96, 146], [106, 150]], false, { width: 3 });
    s.path([[124, 150], [134, 146], [144, 150]], false, { width: 3 });
    s.path([[94, 184], [112, 190], [130, 184]], false, { width: 2.8 });
  },
  highlighter_wyvern(s) {
    // tail
    s.path([[176, 186], [214, 196], [232, 176], [244, 186]], false, { color: '#8fcf3c', width: 6, passes: 1 });
    s.path([[176, 186], [214, 196], [232, 176], [244, 186]], false, { width: 2 });
    // glowing wings
    s.poly([[132, 140], [168, 46], [226, 70], [180, 150]], { fill: '#e8ff7a', width: 2.6 });
    s.line(168, 46, 176, 146, { width: 1.8, passes: 1 });
    s.line(226, 70, 178, 148, { width: 1.8, passes: 1 });
    // body
    s.ellipse(150, 170, 52, 32, { fill: '#c8f560' });
    s.line(132, 198, 126, 236);
    s.line(166, 198, 172, 236);
    // neck + highlighter-cap head (faces left)
    s.poly([[112, 162], [86, 118], [100, 110], [128, 154]], { fill: '#c8f560', width: 2.6 });
    s.poly([[52, 96], [96, 92], [104, 120], [58, 126]], { fill: '#8fcf3c' });
    s.poly([[52, 96], [36, 106], [58, 126]], { fill: INK.yellow, width: 2.2 });
    s.dot(76, 104, 4);
    for (const [x, y] of [[30, 150], [62, 168], [100, 216]] as [number, number][]) s.ellipse(x, y, 5, 8, { fill: '#c8f560', width: 1.6 });
  },
  principal_shadow(s) {
    // tall scribbled body in a suit
    s.poly([[92, 104], [164, 104], [178, 236], [78, 236]], { fill: '#4b3a6b' });
    for (let y = 120; y < 230; y += 18) s.scribble(88, y, 80, 6, 2, { color: '#2e2244', width: 2 });
    // tie
    s.poly([[122, 106], [134, 106], [138, 150], [128, 164], [118, 150]], { fill: INK.red, width: 2.2 });
    // clipboard (held out to the left)
    s.line(92, 140, 66, 152);
    s.rect(30, 128, 40, 54, { fill: '#c89b6d', width: 2.4 });
    s.rect(36, 136, 28, 40, { fill: INK.white, width: 1.6, passes: 1 });
    for (let y = 144; y < 172; y += 8) s.line(40, y, 60, y, { width: 1.4, passes: 1 });
    // head with glowing eyes behind glasses
    s.ellipse(128, 70, 32, 34, { fill: '#4b3a6b' });
    s.ellipse(114, 68, 11, 9, { width: 2.4 });
    s.ellipse(142, 68, 11, 9, { width: 2.4 });
    s.line(125, 68, 131, 68, { width: 2 });
    s.dot(114, 68, 4, INK.yellow);
    s.dot(142, 68, 4, INK.yellow);
    s.line(102, 54, 124, 60, { width: 3 });
    s.line(154, 54, 132, 60, { width: 3 });
  },
};

/** Rock / paper / scissors pictograms (128×128). */
export const MOVE_DOODLES: Record<'rock' | 'paper' | 'scissors', DoodleFn> = {
  rock(s) {
    s.poly([[22, 88], [30, 46], [62, 26], [100, 36], [112, 76], [94, 106], [44, 108]], { fill: '#9aa7b0', width: 3.2 });
    s.line(50, 56, 64, 66, { width: 2, passes: 1 });
    s.line(80, 50, 90, 60, { width: 2, passes: 1 });
    s.path([[52, 86], [64, 92], [76, 86]], false, { width: 2.2 });
  },
  paper(s) {
    s.poly([[28, 18], [86, 18], [104, 36], [104, 112], [28, 112]], { fill: INK.white, width: 3 });
    s.poly([[86, 18], [86, 36], [104, 36]], { width: 2.4 });
    for (let y = 50; y < 104; y += 14) s.line(38, y, 94, y, { color: INK.lineBlue, width: 2, passes: 1 });
    s.line(42, 22, 42, 108, { color: INK.margin, width: 1.6, passes: 1 });
  },
  scissors(s) {
    s.ellipse(34, 92, 16, 13, { fill: INK.red });
    s.ellipse(66, 106, 16, 13, { fill: INK.red });
    s.poly([[44, 82], [112, 22], [118, 28], [56, 92]], { fill: '#c8d0d8', width: 2.6 });
    s.poly([[60, 96], [112, 52], [116, 60], [70, 104]], { fill: '#c8d0d8', width: 2.6 });
    s.dot(62, 88, 3.4);
  },
};

/** Small effect sprites (64×64 unless noted). */
export const FX_DOODLES: Record<string, DoodleFn> = {
  paper_scrap(s) {
    s.poly([[14, 18], [46, 10], [52, 40], [20, 50]], { fill: INK.white, width: 2 });
    s.line(20, 28, 44, 22, { color: INK.lineBlue, width: 1.6, passes: 1 });
  },
  eraser_crumb(s) {
    s.scribble(14, 32, 34, 6, 3, { color: '#e79aae', width: 4 });
  },
  star(s) {
    s.star(32, 32, 24, { fill: INK.yellow, width: 2.4 });
  },
  heart(s) {
    s.poly([[32, 54], [10, 30], [14, 14], [32, 22], [50, 14], [54, 30]], { fill: INK.pink, width: 2.4 });
  },
  shaving(s) {
    const pts: [number, number][] = [];
    for (let i = 0; i < 20; i++) {
      const a = i * 0.6;
      const r = 4 + i * 1.1;
      pts.push([32 + Math.cos(a) * r, 32 + Math.sin(a) * r]);
    }
    s.path(pts, false, { color: '#c89b6d', width: 3 });
  },
  impact(s, size) {
    const c = size / 2;
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const r1 = size * (0.16 + (i % 2) * 0.06);
      const r2 = size * (0.42 + (i % 3) * 0.03);
      s.line(c + Math.cos(a) * r1, c + Math.sin(a) * r1, c + Math.cos(a) * r2, c + Math.sin(a) * r2, { width: 4 });
    }
  },
  pow(s, size) {
    const c = size / 2;
    const pts: [number, number][] = [];
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const r = i % 2 === 0 ? size * 0.46 : size * 0.3;
      pts.push([c + Math.cos(a) * r, c + Math.sin(a) * r]);
    }
    s.poly(pts, { fill: INK.yellow, width: 3 });
    s.text('POW!', c, c + 2, size * 0.24, INK.red, -0.12);
  },
  sweat(s) {
    s.poly([[32, 10], [44, 36], [32, 52], [20, 36]], { fill: INK.lineBlue, width: 2 });
  },
};
